import styles from './Avatar.module.css';

interface AvatarProps {
  /** Initials shown when no image is provided. Should be 1-2 characters. */
  initials: string;
  /** Optional photo URL — when provided, replaces the initials fallback. */
  photoUrl?: string;
  /** Pixel size. Defaults to 36. */
  size?: number;
  /** Accessible label. Falls back to the initials if omitted. */
  label?: string;
  className?: string;
}

/**
 * Circular user avatar with deterministic gradient fallback derived from the
 * initials so a given user lands on the same color across sessions.
 */
export function Avatar({ initials, photoUrl, size = 36, label, className }: AvatarProps) {
  const gradientIndex = hash(initials) % AVATAR_GRADIENTS.length;
  const gradient = AVATAR_GRADIENTS[gradientIndex];

  return (
    <span
      className={[styles.avatar, className].filter(Boolean).join(' ')}
      style={{ width: size, height: size, background: photoUrl ? undefined : gradient }}
      role="img"
      aria-label={label ?? initials}
    >
      {/* Provider identity (photo, initials) is not PHI and is visible in
          LogRocket session replay; the session is keyed by the provider. */}
      {photoUrl ? (
        <img src={photoUrl} alt="" className={styles.image} />
      ) : (
        <span className={styles.initials} style={{ fontSize: Math.round(size * 0.4) }}>
          {initials}
        </span>
      )}
    </span>
  );
}

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #30858E, #124C5C)',
  'linear-gradient(135deg, #235E6E, #002B2A)',
  'linear-gradient(135deg, #B0CCC9, #30858E)',
  'linear-gradient(135deg, #124C5C, #002B2A)',
  'linear-gradient(135deg, #F7A66A, #FF8A75)',
];

const hash = (str: string): number => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};
